import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import api from "../api/axiosConfig";
import { toast } from "react-toastify";
import { usePermissionFetch } from "../hooks/usePermissionFetch";


const ProjectContext = createContext();

export const PROJECT_STAGES = [
  "Draft", "Confirmed", "Internal Approved", "Govt. Approved",
  "In Progress", "Ready to Possession", "Ready to Move", "Canceled"
];

const DEFAULT_CONSTRUCTION_STAGES = [
  { name: "Planning & Design",           weight: 10, completed: 0, status: "Pending" },
  { name: "Excavation & Foundation",     weight: 20, completed: 0, status: "Pending" },
  { name: "Structure (Superstructure)",  weight: 30, completed: 0, status: "Pending" },
  { name: "MEP (Mech, Elec, Plumbing)", weight: 15, completed: 0, status: "Pending" },
  { name: "Finishing (Int/Ext)",         weight: 15, completed: 0, status: "Pending" },
  { name: "Handover & Closing",          weight: 10, completed: 0, status: "Pending" },
];

export const ProjectProvider = ({ children }) => {
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const {
    data: fetchedProjects,
    loading,
    refetch: fetchProjects,
  } = usePermissionFetch({
    permission: 'project.view',
    url: '/projects',
    fallback: [],
    lazy: false,
  });

  const projects = fetchedProjects || [];

  // 🟢 Dedicated single project fetch (memoized & decoupled from list mutations)
  const fetchProjectById = useCallback(async (id) => {
    if (!id) return null;
    try {
      const res = await api.get(`/projects/${id}`);
      return res.data?.data || res.data;
    } catch (err) {
      console.error("Single project fetch error:", err);
      return null;
    }
  }, []);

  const calculateTotalProgress = useCallback((stages) => {
    if (!stages || stages.length === 0) return 0;
    const total = stages.reduce((acc, stage) => acc + (stage.weight * stage.completed) / 100, 0);
    return Math.min(Math.round(total), 100);
  }, []);

  const addProject = async (projectData) => {
    const payload = {
      name:               "New Project",
      client:             "",
      description:        "",
      status:             "Draft",
      stage:              "Draft",
      startDate:          "",
      endDate:            "",
      budget:             0,
      currency:           "USD",
      constructionStages: JSON.parse(JSON.stringify(DEFAULT_CONSTRUCTION_STAGES)),
      progress:           0,
      stakeholders:       [],
      documents:          [],
      tags:               [],
      subUnits:           [],
      ...projectData,
    };
    try {
      const res = await api.post("/projects", payload);
      await fetchProjects();
      return res.data.id;
    } catch (err) {
      console.error("Error creating project:", err);
      toast.error("Failed to create project");
      return null;
    }
  };

  const updateProject = useCallback(async (id, updatedData, silent = false) => {
    const currentProject = projects.find(p => String(p.id) === String(id));
    try {
      const apiPayload = { ...currentProject, ...updatedData };
      await api.put(`/projects/${id}`, apiPayload);
      if (!silent) toast.success("Project updated successfully");
    } catch (err) {
      console.error("Error updating project:", err);
      toast.error("Failed to update project");
      if (mountedRef.current) fetchProjects();
    }
  }, [projects, fetchProjects]);

  const updateProjectStage = useCallback((projectId, stageIndex, newCompletion, newStatus) => {
    const project = projects.find(p => String(p.id) === String(projectId));
    if (!project) return;
    const updatedStages = [...(project.constructionStages || [])];
    if (updatedStages[stageIndex]) {
      updatedStages[stageIndex] = {
        ...updatedStages[stageIndex],
        completed: Number(newCompletion),
        status:    newStatus || updatedStages[stageIndex].status,
      };
    }
    const newProgress = calculateTotalProgress(updatedStages);
    updateProject(projectId, { constructionStages: updatedStages, progress: newProgress }, true);
  }, [projects, calculateTotalProgress, updateProject]);

  const deleteProject = async (id) => {
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Project deleted");
      fetchProjects();
    } catch (err) {
      console.error("Error deleting project:", err);
      toast.error("Failed to delete project");
      if (mountedRef.current) fetchProjects();
    }
  };

  const getProjectById = useCallback((id) => {
    const proj = projects.find(p => String(p.id) === String(id));
    if (!proj) return undefined;
    if (!proj.constructionStages || proj.constructionStages.length === 0) {
      return { ...proj, constructionStages: JSON.parse(JSON.stringify(DEFAULT_CONSTRUCTION_STAGES)) };
    }
    return proj;
  }, [projects]);

  return (
    <ProjectContext.Provider value={{
      projects, loading, addProject, updateProject, deleteProject,
      getProjectById, fetchProjectById, PROJECT_STAGES, updateProjectStage, fetchProjects,
    }}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProjects = () => useContext(ProjectContext);